import { Injectable, Signal, signal } from '@angular/core';
import { FiSignalRService } from '../../shared/fi-signal-r/fi-signal-r.service';
import { Subject } from 'rxjs';
import { bufferDebounce } from '../../shared/utils/buffer-debounce';
import { filter, map, takeUntil } from 'rxjs/operators';
import { IOnboardingCompletionChangedEventItem } from '@fi-shared/fi-signal-r/fi-signal-r';
import {
    ICurrentOpenedCompletionStepData,
    IOnboardingCompletionUserStep,
} from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';

interface IOnboardingCompletionChangedData {
    userCompletionStep: IOnboardingCompletionUserStep;
    completionChangedEventItem: IOnboardingCompletionChangedEventItem;
}

@Injectable()
export class FiOnboardingCompletionSyncService {
    constructor(private fiSignalRService: FiSignalRService) {}

    private subscribeToOnboardingCompletionChangedInternal(args: {
        callback: (completionChangedEventData: IOnboardingCompletionChangedEventItem[]) => void;
        onDestroy$?: Subject<void>;
        debounceMs?: number;
    }) {
        const itemUpdated$ = new Subject<IOnboardingCompletionChangedEventItem>();

        itemUpdated$
            .pipe(
                args.onDestroy$ ? takeUntil(args.onDestroy$) : (v) => v,
                bufferDebounce(args.debounceMs ?? 1000),
                filter((ids) => !!ids.length),
                map((ids) => [...new Set(ids)]), // receive unique ids
            )
            .subscribe((updatedItemIds) => {
                args.callback(updatedItemIds);
            });

        this.fiSignalRService.onboardingCompletionChangedEvent.subscribe((data) => {
            data.Completions.forEach((completion) => itemUpdated$.next(completion));
        });
    }

    // note: the fiSignalRService.onboardingCompletionChangedEvent is emitted only for quiz timeouts
    subscribeToOnboardingCompletionChanged(args: {
        onboardingId: number;
        userId: number;
        userCompletionSteps: IOnboardingCompletionUserStep[];
        onDestroy$: Subject<void>;
        callback: (updatedCompletions: IOnboardingCompletionChangedData[]) => void;
    }) {
        this.subscribeToOnboardingCompletionChangedInternal({
            onDestroy$: args.onDestroy$,
            callback: (completionChangedEventData) => {
                const updatedCompletions: IOnboardingCompletionChangedData[] = [];

                completionChangedEventData.forEach((completionChangedEventItem) => {
                    const userCompletionStep = args.userCompletionSteps.find((completionStep) => {
                        return (
                            completionChangedEventItem.OnboardingId === args.onboardingId &&
                            completionChangedEventItem.UserId === args.userId &&
                            completionChangedEventItem.OnboardingStepId === completionStep.OnboardingStepId
                        );
                    });
                    if (userCompletionStep) {
                        updatedCompletions.push({
                            userCompletionStep,
                            completionChangedEventItem,
                        });
                    }
                });
                if (updatedCompletions.length > 0) {
                    args.callback(updatedCompletions);
                }
            },
        });
    }

    updateUserCompletions(updatedCompletions: IOnboardingCompletionChangedData[]) {
        updatedCompletions.forEach((item) => {
            const completionChangedEventItem = item.completionChangedEventItem;
            item.userCompletionStep.CompletionState = completionChangedEventItem.OnboardingStepCompletionState;
            if (item.userCompletionStep.QuizCompletion) {
                item.userCompletionStep.QuizCompletion.CompletionState = completionChangedEventItem.QuizCompletionState;
            }
        });
    }

    private _currentOpenedCompletionStepData = signal<ICurrentOpenedCompletionStepData | undefined>(undefined);
    readonly currentOpenedCompletionStepData: Signal<ICurrentOpenedCompletionStepData | undefined> =
        this._currentOpenedCompletionStepData.asReadonly();
    setCurrentOpenedCompletionStepData(data: ICurrentOpenedCompletionStepData) {
        this._currentOpenedCompletionStepData.set(data);
    }
}
